// HeroEmailStart — inline single-field email capture on the landing hero.
// Replaces the legacy 3-button CTA cluster. Posts the email straight to
// the existing zero-click trial endpoint so the prospect goes from
// "saw the headline" → "magic link sent" in under 5 seconds, no signup
// form click-through.
import { useState } from "react";
import { ArrowRight, Loader2, CheckCircle2, Sparkles, Mail } from "lucide-react";
import { trackWithPersonalization } from "@/lib/analytics";
import { getAttribution, newEventId, firePixelLead } from "@/lib/attribution";
import { useBotShield, BotHoneypot } from "@/lib/botShield";
import { useT } from "@/lib/i18n";
import { emailError } from "@/lib/validation";

export default function HeroEmailStart({ personal }) {
  const t = useT();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  // `touched` = user has left the field at least once. We ONLY show the
  // "invalid email" hint after touched=true, so typing the first letter
  // ("c") doesn't immediately trigger a red-border scream. This matches
  // the material-design + Apple HIG pattern users expect.
  const [touched, setTouched] = useState(false);
  const shield = useBotShield();

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    const clean = (email || "").toLowerCase().trim();
    // Match RFC-5321 basic shape — same rules as pydantic EmailStr so we
    // never send the backend a value it will reject with a raw JSON error.
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(clean);
    if (!emailOk) {
      setError(t("trial.email_error", "Please enter a valid email address (e.g. you@example.ca)."));
      return;
    }
    setBusy(true);
    setError("");
    const eventId = newEventId();
    // Fire the browser pixel BEFORE the network call so the Meta CAPI
    // event (fired by the backend) can deduplicate against it via eventID.
    firePixelLead(eventId);
    try {
      const attrib = getAttribution();
      const r = await fetch("/api/trial/magic-link/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: clean,
          ...attrib,
          ...shield.values(),
          event_id: eventId,
          event_source_url: typeof window !== "undefined" ? window.location.href : "",
        }),
      });
      if (!r.ok) {
        // FastAPI returns { detail: [{msg: "..."}] } for 422 — extract the
        // human-readable msg. For other errors, fall back to a friendly line.
        let friendly = "Something went wrong — please try again.";
        try {
          const j = await r.json();
          if (Array.isArray(j?.detail) && j.detail[0]?.msg) {
            const msg = String(j.detail[0].msg);
            // pydantic returns things like "value is not a valid email address: ..."
            friendly = msg.startsWith("value is not a valid email")
              ? t("trial.email_error", "Please enter a valid email address (e.g. you@example.ca).")
              : msg.replace(/^value_error,?\s*/i, "");
          } else if (typeof j?.detail === "string") {
            friendly = j.detail;
          }
        } catch { /* body wasn't JSON — keep the generic message */ }
        throw new Error(friendly);
      }
      setSent(true);
      try {
        trackWithPersonalization("lead_capture", personal,
          { source: "hero_email", cta_label: "hero-magic-link" });
        trackWithPersonalization("trial_start_submitted", personal,
          { source: "hero_email", email_domain: clean.split("@")[1] || "" });
      } catch { /* analytics is best-effort */ }
    } catch (e2) {
      setError(e2?.message || "Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="p-5 rounded-2xl bg-emerald-50 border-2 border-emerald-400 max-w-xl" data-testid="hero-email-sent">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-none mt-0.5"/>
          <div>
            <div className="font-display font-bold text-base text-slate-900">Check your inbox to activate your trial.</div>
            <p className="text-sm text-slate-700 mt-1 leading-relaxed">
              We just emailed <strong>{email}</strong>. Tap <b>Activate my trial</b> and your account is ready in under 60 seconds — no password required.
            </p>
            <button onClick={() => { setSent(false); setEmail(""); }}
              data-testid="hero-email-reset"
              className="text-xs font-bold text-emerald-700 hover:text-emerald-900 mt-2 inline-flex items-center gap-1">
              Use a different email
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Inline validation state — only surface the "invalid email" hint AFTER
  // the visitor has BLURRED the field (touched=true). Typing the first
  // character no longer trips the red border. On submit, the check runs
  // regardless because that path sets touched=true itself.
  const liveError = touched && email ? emailError(email) : "";
  const canSubmit = !busy && (!email || !emailError(email));

  return (
    <form onSubmit={submit} className="max-w-xl" data-testid="hero-email-form">
      <BotHoneypot {...shield.fieldProps} />
      <div className="flex flex-col sm:flex-row gap-2 items-stretch">
        <div className="relative flex-1">
          <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"/>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder={t("landing.email_placeholder", "Email")}
            required
            aria-invalid={!!liveError}
            data-testid="hero-email-input"
            className={`w-full pl-10 pr-3 py-3.5 rounded-xl border-2 focus:ring-2 outline-none text-base bg-white transition-colors ${
              liveError
                ? "border-rose-400 focus:border-rose-500 focus:ring-rose-100"
                : "border-slate-300 focus:border-emerald-500 focus:ring-emerald-100"
            }`}/>
        </div>
        <button type="submit"
          disabled={!canSubmit}
          aria-disabled={!canSubmit}
          data-testid="hero-email-submit"
          className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[15px] transition-colors shadow-lg shadow-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-emerald-600 whitespace-nowrap">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
          {t("landing.cta_trial", "Choose a plan")} <ArrowRight className="w-4 h-4"/>
        </button>
      </div>
      {(liveError || error) && (
        <div className="mt-2 text-xs text-rose-700 font-semibold" data-testid="hero-email-error">
          {error || liveError}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-slate-600">
        <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> {t("landing.no_card", "Cancel any time")}</span>
        <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> {t("landing.setup", "90-second setup")}</span>
        <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> {t("landing.cancel", "Cancel anytime")}</span>
      </div>
    </form>
  );
}
